import { createContext, useContext, useEffect, useReducer } from 'react';

import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { t } from 'i18next';

import { useModalContext } from '../../context/Modal';

import { AddAlert } from '../Widget/Portfolio/AddAlert';
import { useUserStrategies } from '../../context/UserStrategies';

import {
  GET_INSPIRED_ASSETS,
  LIST_ASSET_FAVORITES,
  SEARCH_ASSET_BY_TYPE_AND_EXCHANGE,
  TOGGLE_LIST_FAVORITES,
} from '../../http/apollo-queries';

import { IAsset } from '../../types/model';
import {
  ColumnsMarket,
  InspiredAssets,
  ListFavoritesAssets,
  MarketAssetType,
  MarketContextState as State,
  SearchAssetsByTypeAndExchange,
  ToggleParam,
} from './types';
import { UpgradeSubscription } from '../UpgradeSubscription/UpgradeSubscription';
import { useAuth } from '../../context/Auth';
import { UpsertAsset } from '../Asset/UpsertAsset';
import { StrategyAssetCategoryTypeEnum } from '../../constants/enums';

const reducer = (state: State, updates: Partial<State>): State => {
  return { ...state, ...updates };
};

const LIMIT = 50;

// this state will be updated by the 'reducer' function
const localState: State = {
  assetsList: [],
  assetType: 'CRYPTO',
  assetExchange: '',
  extractedAssets: {
    CRYPTO: [],
    STOCK: [],
    BOND: [],
    FOREX: [],
    ETF: [],
    FUND: [],
    // OPTION: [],
  },
  favoritesAssetsIds: [],

  // filters
  filterFavourites: false,
  filterMerlin: false,
  filterPortfolio: false,

  isMarketFilterVisible: false,
  isUpgradeModalOpen: '',
  limit: LIMIT,
  loadingExtractAssets: false,
  noActiveFilter: false,
  tokens: {
    BOND: null,
    CRYPTO: null,
    FOREX: null,
    STOCK: null,
    ETF: null,
    FUND: null,
    // OPTION: null,
  },

  addAlert() {},
  addAssetToStrategy() {},
  dispatch() {},
  handleStarClick: () => new Promise((r) => r),
  handleAddAlert() {},
  handleUpgradeModalOpen() {},
  loadMore: () => new Promise((r) => r()),
  resetFilters() {},
  setAssetType() {},
  setAssetExchange() {},
  setInitialFilters() {},
  toggle() {},
};

const MarketsContext = createContext(localState);

const BASE_COLUMNS = ['ASSET', 'PRICE', 'VAR 24H', 'VAR 7D'] as const;

export const COLUMNS_MARKET: ColumnsMarket = {
  CRYPTO: [...BASE_COLUMNS, 'MARKET CAP', 'ECOSYSTEM'],

  STOCK: [...BASE_COLUMNS, 'MARKET CAP', 'INDUSTRY'],

  BOND: [...BASE_COLUMNS, 'COUPON', 'MATURITY DATE', 'RATING'],

  FOREX: [...BASE_COLUMNS, 'COUNTRY', 'AVG30D', 'YTH'],

  ETF: [...BASE_COLUMNS, 'CATEGORY', 'COMPANY', 'YIELD'],

  FUND: [
    ...BASE_COLUMNS.slice(0, 2),
    'NAV',
    'YIELD',
    'YIELD 1 YEAR',
    'YIELD 3 YEAR',
    'YIELD 5 YEAR',
  ],
  // OPTION: [
  //   ...BASE_COLUMNS.slice(0, 2),
  //   'TYPE',
  //   'STRIKE',
  //   'EXPIRATION DATE',
  //   'VOLUME',
  //   'OPEN INT',
  // ],
};

const extractUniqueAssets = (
  objects: { asset: IAsset }[],
  assetType: MarketAssetType
) =>
  Object.values(
    objects.reduce(
      (obj, { asset }) => {
        !obj[asset.id] && asset.type === assetType && (obj[asset.id] = asset);

        return obj;
      },

      {} as { [key: string]: IAsset }
    )
  );

export const MarketsProvider = ({ children }: { children: JSX.Element }) => {
  const { user } = useAuth();

  const { dispatch: dispatchModal, closeModal } = useModalContext();
  const { strategies } = useUserStrategies();

  const [state, dispatch] = useReducer(reducer, localState);

  const {
    assetType,
    assetExchange,
    extractedAssets,
    filterFavourites,
    filterMerlin,
    filterPortfolio,
    limit,
  } = state;

  const [toggleFavoriteAssets] = useMutation(TOGGLE_LIST_FAVORITES, {
    onCompleted: () => refetchFavouritesAssets(),
  });

  const {
    data: { listAssetFavorites: { items: favorites = [] } = {} } = {},
    refetch: refetchFavouritesAssets,
  } = useQuery<ListFavoritesAssets>(LIST_ASSET_FAVORITES, {
    skip: !user?.profile?.id,
  });

  const { data: { getInspiredAssets = [] } = {} } = useQuery<InspiredAssets>(
    GET_INSPIRED_ASSETS,
    {
      skip: !user?.profile?.id,
    }
  );

  const [fetchAssets, { loading: loadingExtractAssets, fetchMore }] =
    useLazyQuery<SearchAssetsByTypeAndExchange>(
      SEARCH_ASSET_BY_TYPE_AND_EXCHANGE,
      {
        variables: {
          limit,
          nextToken: null,
          type: assetType,
          exchange:
            assetType === 'STOCK' && assetExchange
              ? assetExchange.split(',')
              : [],
          sortDirection: 'ASC',
        },
        // skip: !user?.profile?.id,
        fetchPolicy: 'network-only',
        onCompleted: ({
          searchAssetsByTypeAndExchange: { items, nextToken },
        }) =>
          dispatch({
            extractedAssets: {
              ...extractedAssets,
              ...(extractedAssets[assetType]?.length === 0 && {
                [assetType]: [...items],
              }),
            },
            tokens: { ...state.tokens, [assetType]: nextToken },
          }),
        onError: (e) =>
          console.error({ component: 'Market context', error: e }),
      }
    );

  useEffect(() => {
    if (!user?.profile?.id) {
      return;
    }

    fetchAssets().then((res) => {
      const items = res.data?.searchAssetsByTypeAndExchange?.items || [];
      const nextToken = res.data?.searchAssetsByTypeAndExchange?.nextToken;

      dispatch({
        extractedAssets: {
          ...extractedAssets,
          [assetType]: [...items],
        },
        tokens: { ...state.tokens, [assetType]: nextToken },
      });
    });
  }, [user?.profile?.id, assetExchange]);

  const favouritesAssets = extractUniqueAssets(favorites, assetType);
  const merlinAssets = extractUniqueAssets(getInspiredAssets, assetType);
  const portfolioAssets = extractUniqueAssets(
    strategies.map(({ strategyAssets: { items } }) => items).flat(),
    assetType
  );

  const favoritesAssetsIds = favouritesAssets.map(({ id }) => id);
  const merlinAssetsIds = merlinAssets.map(({ id }) => id);
  const portfolioAssetsIds = portfolioAssets.map(({ id }) => id);

  const noActiveFilter = !(filterFavourites || filterMerlin || filterPortfolio);

  const idsFilter = [
    [filterFavourites, favoritesAssetsIds],
    [filterPortfolio, portfolioAssetsIds],
    [filterMerlin, merlinAssetsIds],
  ].reduce(
    (arr, [isActive, ids]) => (isActive ? [...arr, ids] : arr),
    []
  ) as string[][];

  const assetsList = noActiveFilter
    ? extractedAssets[assetType]
    : (filterPortfolio
        ? portfolioAssets
        : filterMerlin
        ? merlinAssets
        : favouritesAssets
      ).filter(({ id }) => idsFilter.every((arr) => arr.includes(id)));

  const addAlert = (asset: IAsset) =>
    dispatchModal({
      element: <AddAlert asset={asset} />,
      title: t('markets.addAlert'),
    });

  const addAssetToStrategy = (id: string, name: string, type: string) => {
    const canAddAsset =
      user?.membership.membershipPlan.features.strategyAssets === -1 ||
      strategies.some(
        ({ strategyAssets: { items } }) =>
          items.length < user!.membership.membershipPlan.features.strategyAssets
      );

    canAddAsset
      ? dispatchModal({
          element: (
            <UpsertAsset
              asset={{ id, name }}
              assetCategory={{
                id: '',
                title: type,
                type: StrategyAssetCategoryTypeEnum.SYSTEM,
                key: type,
              }}
              onCloseClick={closeModal}
              action={'ADD_TO_STRATEGY'}
            />
          ),
          title: t('markets.addAsset'),
        })
      : handleUpgradeModalOpen('ASSETS');
  };

  const toggle = (key: ToggleParam) => dispatch({ [key]: !state[key] });

  const handleUpgradeModalOpen = (value: 'ASSETS' | 'SMART_ALERT' | '') => {
    dispatch({
      isUpgradeModalOpen: value,
    });
  };

  const handleAddAlert = (asset: IAsset) => {
    user?.membership?.membershipPlan?.features?.smartAlert
      ? addAlert(asset)
      : handleUpgradeModalOpen('SMART_ALERT');
  };

  const handleStarClick = (assetId: string) => {
    return toggleFavoriteAssets({ variables: { assetId } });
  };

  const loadMore = () =>
    fetchMore?.({
      variables: {
        limit,
        nextToken: state.tokens[assetType],
        exchange: state.assetExchange ? state.assetExchange.split(',') : [],
        type: assetType,
        sortDirection: 'ASC',
      },
    }).then(
      ({
        data: {
          searchAssetsByTypeAndExchange: { items, nextToken },
        },
      }) => {
        dispatch({
          extractedAssets: {
            ...extractedAssets,
            [assetType]: [...extractedAssets[assetType], ...items],
          },
          tokens: { ...state.tokens, [assetType]: nextToken },
        });
      }
    );

  const resetFilters = () =>
    dispatch({
      filterFavourites: false,
      filterMerlin: false,
      filterPortfolio: false,
    });

  const setInitialFilters = (type: MarketAssetType, filters: string[]) => {
    dispatch({
      ...(type !== assetType && { assetType: type }),
      ...(filters.includes('favourites') && { filterFavourites: true }),
      ...(filters.includes('portfolio') && { filterPortfolio: true }),
      ...(filters.includes('merlin') && { filterMerlin: true }),
    });
  };

  const setAssetType = (assetType: State['assetType']) =>
    dispatch({ assetType });

  const setAssetExchange = (assetExchange: State['assetExchange']) =>
    dispatch({ assetExchange });

  const value = {
    ...state,
    assetsList,
    favoritesAssetsIds,
    loadingExtractAssets,
    noActiveFilter,
    addAlert,
    addAssetToStrategy,
    dispatch,
    fetchMore,
    handleAddAlert,
    handleStarClick,
    handleUpgradeModalOpen,
    loadMore,
    resetFilters,
    setAssetType,
    setAssetExchange,
    setInitialFilters,
    toggle,
    toggleFavoriteAssets,
  };

  return (
    <MarketsContext.Provider value={value}>
      <>
        <UpgradeSubscription
          isOpen={state.isUpgradeModalOpen.length > 0}
          onCloseClick={() => handleUpgradeModalOpen('')}
          feature={state.isUpgradeModalOpen}
          viewMode={'modal'}
        />
        {children}
      </>
    </MarketsContext.Provider>
  );
};

export const useMarketsContext = () => useContext(MarketsContext);
